import { useEffect, useState } from 'react';
import axios from 'axios';
import { BANNER_TYPE } from '@amzn/aac-chrome-internal-api';
import { PlacementType } from '@amzn/aces-communications-api';
import {
  LocalNotification,
  StardustCloudfrontResponse,
  UseStardustBannersResponse,
} from './types';
import { UseStardustBannersMetricsPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useSelector } from 'react-redux';
import { selectChromeConfiguration } from 'src/app/slice';

export const useStardustBanners = (
  placement: PlacementType,
  cloudfrontURL: string,
  isStardustBannerFeatureEnabled: boolean,
  isStardustPageLevelBannerFeatureEnabled: boolean,
): UseStardustBannersResponse => {
  const [localNotifications, setLocalNotifications] = useState<
    LocalNotification[]
  >([]);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const chromeConfiguration = useSelector(selectChromeConfiguration);
  const axCommonMetricName = 'axconsole_common';
  const adspMetricName = 'axconsole_adsp';
  const axAppLevelMetricName = 'axconsole_appLevel';
  const axPageLevelMetricName = 'axconsole_pageLevel';

  useEffect(() => {
    // only support INFORMATION, ERROR, WARNING messageType:
    // https://w.amazon.com/bin/view/Advertising_Console/Advertising_Console_Experience/Console_Banners
    // skip other messageTypes
    const refreshInterval = 100000;
    const { marketplaceId, locale, applicationName } = internalChromeConfig;
    const { pageId } = chromeConfiguration;
    // There is a case where locale is undefined that is causing a type error
    // when calling `locale.replace`.  Adding this code to default to en-US
    // banners for stardust if there is an error with the locale from
    // chrome internal context.
    if (!locale) {
      logger.info('locale is not defined in internal chrome context.', {
        ...internalChromeConfig,
        uri: location.href,
      });
    }

    const compatibleLocale = (locale || 'en-US').replace('_', '-');
    const axUrl = `${cloudfrontURL}/axconsole/common/${marketplaceId}/${compatibleLocale}`;
    const adspUrl = `${cloudfrontURL}/axconsole/adsp/common/${marketplaceId}/${compatibleLocale}`;
    const appLevelBannerUrl = `${cloudfrontURL}/axconsole/pagelevel/${applicationName}/${marketplaceId}/${compatibleLocale}`;
    const pageLevelBannerUrl = `${cloudfrontURL}/axconsole/pagelevel/${applicationName}/${pageId}/${marketplaceId}/${compatibleLocale}`;

    /**
     * Execute once initially and then execute every 10 mins
     */
    async function retrieveStardustBanners(url: string, metricName: string) {
      try {
        const s3Object = await axios.get<StardustCloudfrontResponse[]>(url);
        const response = s3Object.data;
        return convertStardustMessagesToLocalNotification(response);
      } catch (err: any) {
        UseStardustBannersMetricsPublisher.publishCounterMonitor(
          `noStardustBanner:${metricName}-${marketplaceId}-${compatibleLocale}`,
          1,
        );
        logger.warn(
          `No Banner found for ${metricName} in the locale ${compatibleLocale} in marketplace ${marketplaceId}`,
          err,
        );
        return [];
      }
    }

    /**
     * Accumulate all the banners and set them as local notifications
     */
    async function setBannersForLocalNotifications() {
      const promises = [retrieveStardustBanners(axUrl, axCommonMetricName)];

      if (internalChromeConfig.isFromRodeo) {
        promises.push(retrieveStardustBanners(adspUrl, adspMetricName));
      }

      const responses = await Promise.all(promises);
      setLocalNotifications(responses.flat());
    }

    /**
     * Accumulate all the page level banners and set them as local notifications
     */
    async function setPageLevelBannersForLocalNotifications() {
      const responses = await Promise.all([
        retrieveStardustBanners(appLevelBannerUrl, axAppLevelMetricName),
        retrieveStardustBanners(pageLevelBannerUrl, axPageLevelMetricName),
      ]);
      setLocalNotifications(responses.flat());
    }

    // Checks if the instance is Global Banner && has a cloudfrontURL and the weblab feature is enabled
    if (
      placement === PlacementType.GLOBAL_BANNER &&
      cloudfrontURL &&
      isStardustBannerFeatureEnabled
    ) {
      setBannersForLocalNotifications();
      const stardustBannerInterval = setInterval(
        setBannersForLocalNotifications,
        refreshInterval,
      );
      return () => clearInterval(stardustBannerInterval);
    } else if (
      placement === PlacementType.PAGE_BANNER &&
      cloudfrontURL &&
      isStardustPageLevelBannerFeatureEnabled
    ) {
      setPageLevelBannersForLocalNotifications();
      const stardustPageBannerInterval = setInterval(
        setPageLevelBannersForLocalNotifications,
        refreshInterval,
      );
      return () => clearInterval(stardustPageBannerInterval);
    } else if (!cloudfrontURL) {
      UseStardustBannersMetricsPublisher.publishCounterMonitor(
        `noCloudfrontURL:${marketplaceId}-${compatibleLocale}`,
        1,
      );
      logger.error(
        `No Cloudfront URL found for the locale ${compatibleLocale} in marketplace ${marketplaceId}`,
      );
      return;
    } else if (
      placement === PlacementType.GLOBAL_BANNER &&
      !isStardustBannerFeatureEnabled
    ) {
      UseStardustBannersMetricsPublisher.publishCounterMonitor(
        `noStardustWeblabEnablement:${marketplaceId}-${compatibleLocale}`,
        1,
      );
      logger.error(
        `Stardust banner Weblab was not enabled for locale ${compatibleLocale} in marketplace ${marketplaceId}`,
      );
      return;
    } else if (
      placement === PlacementType.PAGE_BANNER &&
      !isStardustPageLevelBannerFeatureEnabled
    ) {
      UseStardustBannersMetricsPublisher.publishCounterMonitor(
        `noStardustPageLevelWeblabEnablement:${marketplaceId}-${compatibleLocale}`,
        1,
      );
      logger.error(
        `Stardust page level banner Weblab was not enabled for locale ${compatibleLocale} in marketplace ${marketplaceId}`,
      );
      return;
    }
  }, [
    placement,
    cloudfrontURL,
    isStardustBannerFeatureEnabled,
    isStardustPageLevelBannerFeatureEnabled,
    UseStardustBannersMetricsPublisher,
    logger,
  ]);

  const convertStardustMessagesToLocalNotification = (
    stardustNotifications: StardustCloudfrontResponse[],
  ) => {
    const parsedNotifications: LocalNotification[] = [];
    const currentDateTime = new Date();
    for (const message of stardustNotifications) {
      const validUntil = message['validUntil']
        ? new Date(message['validUntil'])
        : undefined;
      if (validUntil === undefined || validUntil > currentDateTime) {
        parsedNotifications.push({
          id: message.id,
          message: message.description,
          title: message.title ? message.title : undefined,
          type: message.status.toLowerCase() as BANNER_TYPE,
        });
      }
    }
    return parsedNotifications;
  };

  return {
    notifications: localNotifications,
  };
};
